import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { ButtonStyleProps } from '../button.type';
import { buttonBase, getButtonSizeClassName, getFocusClassName, getFocusOutlineClassName } from './common.styles';

const PRIMARY = css`
  background: ${theme.colors.primary50};
  border: none;
  color: ${theme.colors.white};
`;

const PRIMARY_DESTRUCTIVE = css`
  background: ${theme.colors.status.critical};
  border: none;
  color: ${theme.colors.white};
`;

const PRIMARY_DISABLED = css`
  &[aria-disabled='true'] {
    background: ${theme.colors.primary20};
    border-color: ${theme.colors.primary20};
    box-shadow: none;
    color: ${theme.colors.white};
  }
`;

const PRIMARY_DESTRUCTIVE_DISABLED = css`
  &[aria-disabled='true'] {
    background: ${theme.colors.critical20};
    border-color: ${theme.colors.critical20};
    box-shadow: none;
    color: ${theme.colors.white};
  }
`;

const PRIMARY_HOVER = css`
  &:hover:not([aria-disabled='true']) {
    background: ${theme.colors.primary60};
  }
`;

const PRIMARY_DESTRUCTIVE_HOVER = css`
  &:hover:not([aria-disabled='true']) {
    background: ${theme.colors.status.criticalHover};
  }
`;

const PRIMARY_PRESSED = css`
  &:active:not([aria-disabled='true']) {
    background: ${theme.colors.primary70};
    color: ${theme.colors.primary20};

    svg {
      color: ${theme.colors.primary20};
    }
  }
`;

const PRIMARY_DESTRUCTIVE_PRESSED = css`
  &:active:not([aria-disabled='true']) {
    background: ${theme.colors.status.criticalPressed};
    color: ${theme.colors.critical20};

    svg {
      color: ${theme.colors.critical20};
    }
  }
`;

const PRIMARY_LOADING = css`
  background: ${theme.colors.primary10};
  border: 1px solid ${theme.colors.primary30};

  &[aria-disabled='true'] {
    box-shadow: none;
    color: ${theme.colors.white};
  }
`;

const PRIMARY_DESTRUCTIVE_LOADING = css`
  background: ${theme.colors.critical10};
  border: 1px solid ${theme.colors.critical30};

  &[aria-disabled='true'] {
    box-shadow: none;
    color: ${theme.colors.white};
  }
`;

export const primaryClassName = ({
  size,
  iconOnly,
  destructive,
  showFocusOutline,
  isLoading,
}: Pick<ButtonStyleProps, 'size' | 'iconOnly' | 'destructive' | 'showFocusOutline' | 'isLoading'>) => {
  const primaryBtnclasses = destructive
    ? {
        variant: PRIMARY_DESTRUCTIVE,
        disabled: PRIMARY_DESTRUCTIVE_DISABLED,
        hover: PRIMARY_DESTRUCTIVE_HOVER,
        pressed: PRIMARY_DESTRUCTIVE_PRESSED,
        loading: PRIMARY_DESTRUCTIVE_LOADING,
        focus: 'critical' as const,
      }
    : {
        variant: PRIMARY,
        disabled: PRIMARY_DISABLED,
        hover: PRIMARY_HOVER,
        pressed: PRIMARY_PRESSED,
        loading: PRIMARY_LOADING,
        focus: 'primary' as const,
      };

  return cx(
    buttonBase,
    getButtonSizeClassName(size ?? 'small', iconOnly),
    getFocusClassName(primaryBtnclasses.focus),
    showFocusOutline ? getFocusOutlineClassName(primaryBtnclasses.focus) : null,
    primaryBtnclasses.variant,
    !isLoading && primaryBtnclasses.disabled,
    isLoading ? primaryBtnclasses.loading : primaryBtnclasses.hover,
    !isLoading && primaryBtnclasses.pressed
  );
};
